// theme-exempt-file: the snapshot is saved and shown to every viewer, so it keeps Lureo's own colours whatever the saver's theme
import { 
  MapSnapshotRequest,
  MapSnapshotService,
  MapImageProvider,
  MapStorageProvider,
  ProjectDatabaseProvider,
  Story
} from './types';

export class MapSnapshot implements MapSnapshotService {
  constructor(
    private readonly adapter: MapImageProvider & MapStorageProvider & ProjectDatabaseProvider,
    private readonly googleApiKey: string
  ) {}

  async saveProjectMapSnapshot(request: MapSnapshotRequest): Promise<string | null> {
    const { projectInstanceId, stories, userId } = request;

    try {
      const { url: oldUrl } = await this.adapter.getProjectMapImageUrl(projectInstanceId);
      const mapSize = request.mapSettings?.mapSize || { width: 640, height: 640 };
      const mapUrl = this.buildStaticMapUrl(stories, mapSize.width, mapSize.height, request.mapSettings);

      const backgroundImage = await this.adapter.fetchMapImage(mapUrl);
      const finalImage = await this.compositeMarkersOntoMap(backgroundImage, stories, mapSize.width, mapSize.height, request);

      const { url: newUrl, error: storageError } = await this.adapter.storeMapImage({
        image: finalImage,
        projectInstanceId,
        metadata: {
          contentType: 'image/png'
        }
      });

      if (storageError) {
        console.error('MapSnapshot: store map image failed', storageError);
        return null;
      }

      const { error: dbError } = await this.adapter.updateProjectMapImageUrl({
        projectInstanceId,
        imageUrl: newUrl,
        userId
      });

      if (dbError) {
        console.error('MapSnapshot: update project map image URL failed', dbError);
        return null;
      }

      if (oldUrl) {
        const { error: deleteError } = await this.adapter.deleteMapImage(oldUrl);
        if (deleteError) console.warn('MapSnapshot: delete old image failed', deleteError);
      }

      return newUrl;
    } catch (error) {
      console.error('MapSnapshot: saveProjectMapSnapshot failed', error);
      return null;
    }
  }

  private buildStaticMapUrl(stories: Story[], width: number, height: number, mapSettings?: { center: { lat: number; lng: number }; zoom: number; bounds?: { north: number; south: number; east: number; west: number } }): string {
    const valid = stories.filter(s => s.latitude && s.longitude);
    if (valid.length === 0) throw new Error('No story locations');

    const size = `${width}x${height}`;
    const scale = 2;
    const styleParams = this.buildStaticMapStyleParams();
    const apiKey = this.getGoogleApiKey();

    if (mapSettings) {
      const { center, zoom } = mapSettings;
      return `https://maps.googleapis.com/maps/api/staticmap?scale=${scale}&size=${size}&maptype=roadmap&center=${center.lat},${center.lng}&zoom=${zoom}&${styleParams}&key=${apiKey}`;
    }

    const centerLat = valid.reduce((sum, s) => sum + s.latitude!, 0) / valid.length;
    const centerLng = valid.reduce((sum, s) => sum + s.longitude!, 0) / valid.length;
    const zoom = 16;

    return `https://maps.googleapis.com/maps/api/staticmap?scale=${scale}&size=${size}&maptype=roadmap&center=${centerLat},${centerLng}&zoom=${zoom}&${styleParams}&key=${apiKey}`;
  }

  private generateCustomMarkerIcon(story: Story, request: MapSnapshotRequest): string {
    const color = '#87EFD8';
    const displayText = story.order ? `Story ${story.order}` : 'Story 1';
    const fontSize = displayText.length > 7 ? '48' : '64';

    const markerWidth = request.mapSettings?.markerSize?.width || 80;
    const markerHeight = request.mapSettings?.markerSize?.height || 120;
    const viewBoxWidth = request.mapSettings?.svgViewBox?.width || 400;
    const viewBoxHeight = request.mapSettings?.svgViewBox?.height || 600;
    const pinCenterX = request.mapSettings?.svgPinCenter?.x || 200;
    const textYBase = request.mapSettings?.svgTextSettings?.yBase || 280;
    const textOffset = request.mapSettings?.svgTextSettings?.offset || 64;
    const pillHeight = request.mapSettings?.svgTextSettings?.pillHeight || 114;
    const pillRadius = request.mapSettings?.svgTextSettings?.pillRadius || 57;
    
    const svg = `
      <svg width="${markerWidth}" height="${markerHeight}" viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- Pin shadow -->
        <path d="M204,24a88.1,88.1,0,0,0-88,88c0,75.3,80,132.17,83.41,134.55a8,8,0,0,0,9.18,0C212,244.17,292,187.3,292,112A88.1,88.1,0,0,0,204,24Zm0,56a32,32,0,1,1-32,32A32,32,0,0,1,204,80Z" fill="rgba(0,0,0,0.3)"/>
        <!-- Main pin -->
        <path d="M${pinCenterX},16a88.1,88.1,0,0,0-88,88c0,75.3,80,132.17,83.41,134.55a8,8,0,0,0,9.18,0C208,236.17,288,179.3,288,104A88.1,88.1,0,0,0,${pinCenterX},16Zm0,56a32,32,0,1,1-32,32A32,32,0,0,1,${pinCenterX},72Z" fill="${color}"/>
        <!-- Text shadow -->
        <rect x="${pinCenterX - (displayText.length * parseInt(fontSize) * 0.3 + 30) + 2}" y="${textYBase - textOffset - 15 + 4 + textOffset}" width="${displayText.length * parseInt(fontSize) * 0.6 + 60}" height="${pillHeight}" rx="${pillRadius}" ry="${pillRadius}" fill="rgba(0, 0, 0, 0.2)"/>
        <!-- Text background -->
        <rect x="${pinCenterX - (displayText.length * parseInt(fontSize) * 0.3 + 30)}" y="${textYBase - textOffset - 15 + textOffset}" width="${displayText.length * parseInt(fontSize) * 0.6 + 60}" height="${pillHeight}" rx="${pillRadius}" ry="${pillRadius}" fill="rgba(255, 255, 255, 0.95)" stroke="rgba(0, 0, 0, 0.1)" stroke-width="1"/>
        <text x="${pinCenterX}" y="${textYBase + textOffset}" text-anchor="middle" fill="#0B2841" font-family="Cera Pro, sans-serif" font-size="${fontSize}" font-weight="500">${displayText}</text>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  private buildStaticMapStyleParams(): string {
    const styles = [
      { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
      { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
      { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
      { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
      { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
    ];

    const enc = encodeURIComponent;
    const parts: string[] = [];

    styles.forEach((rule) => {
      const seg: string[] = [];
      if ((rule as any).featureType) seg.push(`feature:${(rule as any).featureType}`);
      if ((rule as any).elementType) seg.push(`element:${(rule as any).elementType}`);
      (rule as any).stylers?.forEach((sty: any) => {
        if (sty.color) seg.push(`color:${sty.color.replace('#', '0x')}`);
        if (sty.visibility) seg.push(`visibility:${sty.visibility}`);
      });
      if (seg.length) parts.push(`style=${enc(seg.join('|'))}`);
    });

    return parts.join('&');
  }

  private getGoogleApiKey(): string {
    return this.googleApiKey;
  }

  private async compositeMarkersOntoMap(backgroundBlob: Blob, stories: Story[], width: number, height: number, request: MapSnapshotRequest): Promise<Blob> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    canvas.width = width;
    canvas.height = height;

    const backgroundUrl = URL.createObjectURL(backgroundBlob);
    const backgroundImg = new Image();
    
    return new Promise((resolve, reject) => {
      backgroundImg.onload = async () => {
        try {
          ctx.drawImage(backgroundImg, 0, 0, width, height);

          const validStories = stories.filter(s => s.latitude && s.longitude);
          const centerLat = request.mapSettings?.center.lat || validStories.reduce((sum, s) => sum + s.latitude!, 0) / validStories.length;
          const centerLng = request.mapSettings?.center.lng || validStories.reduce((sum, s) => sum + s.longitude!, 0) / validStories.length;
          const zoom = request.mapSettings?.zoom || 16;

          for (const story of validStories) {
            const x = this.lngToPixel(story.longitude!, centerLng, zoom, width);
            const y = this.latToPixel(story.latitude!, centerLat, zoom, height);

            const markerSvg = this.generateCustomMarkerIcon(story, request);
            const markerImg = await this.svgToImage(markerSvg);

            const markerWidth = request.mapSettings?.markerSize?.width || 80;
            const markerHeight = request.mapSettings?.markerSize?.height || 120;
            const anchorX = request.mapSettings?.markerAnchor?.x || 40;
            const anchorY = request.mapSettings?.markerAnchor?.y || 48;
            
            ctx.drawImage(markerImg, x - anchorX, y - anchorY, markerWidth, markerHeight);
          }

          canvas.toBlob((blob) => {
            URL.revokeObjectURL(backgroundUrl);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/png');
        } catch (error) {
          URL.revokeObjectURL(backgroundUrl);
          reject(error);
        }
      };
      
      backgroundImg.onerror = () => {
        URL.revokeObjectURL(backgroundUrl);
        reject(new Error('Failed to load background image'));
      };
      
      backgroundImg.src = backgroundUrl;
    });
  }

  private async svgToImage(svgDataUri: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load SVG'));
      img.src = svgDataUri;
    });
  }

  private lngToPixel(lng: number, centerLng: number, zoom: number, mapWidth: number): number {
    const worldWidth = 256 * Math.pow(2, zoom);
    const lngOffset = lng - centerLng;
    const pixelsPerDegree = worldWidth / 360;
    return mapWidth / 2 + (lngOffset * pixelsPerDegree);
  }

  private latToPixel(lat: number, centerLat: number, zoom: number, mapHeight: number): number {
    const worldHeight = 256 * Math.pow(2, zoom);
    const latRad = lat * Math.PI / 180;
    const centerLatRad = centerLat * Math.PI / 180;
    const yWorld = worldHeight / 2 - (worldHeight / (2 * Math.PI)) * Math.log(Math.tan(Math.PI / 4 + latRad / 2));
    const centerYWorld = worldHeight / 2 - (worldHeight / (2 * Math.PI)) * Math.log(Math.tan(Math.PI / 4 + centerLatRad / 2));
    
    const yOffset = yWorld - centerYWorld;
    return mapHeight / 2 + yOffset;
  }
}